import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import DictationInput from '../components/DictationInput';
import { toast } from 'sonner';
import { Flag, Plus, CheckCircle, AlertTriangle, Users, Shield } from 'lucide-react';
import { formatDate } from '../lib/utils';

const COMMON_FLAG_REASONS = [
  { value: 'relapse', label: 'Relapse / Substance Use' },
  { value: 'missed_checkin', label: 'Missed Check-In / No-Show' },
  { value: 'housing_instability', label: 'Housing Instability / Eviction Risk' },
  { value: 'employment_loss', label: 'Employment Loss / Job Instability' },
  { value: 'mental_health_crisis', label: 'Mental Health Crisis' },
  { value: 'domestic_violence', label: 'Domestic Violence Concern' },
  { value: 'legal_violation', label: 'Legal / Probation Violation' },
  { value: 'association_risk', label: 'Risky Associations / Peer Influence' },
  { value: 'financial_crisis', label: 'Financial Crisis / No Income' },
  { value: 'family_conflict', label: 'Family Conflict / Custody Issues' },
  { value: 'transportation', label: 'Transportation Barriers' },
  { value: 'medical_emergency', label: 'Medical Emergency' },
  { value: 'non_compliance', label: 'Program Non-Compliance' },
  { value: 'criminal_thinking', label: 'Criminal Thinking Patterns' },
  { value: 'anger_management', label: 'Anger Management Issues' },
  { value: 'isolation', label: 'Social Isolation / Withdrawal' },
  { value: 'other', label: 'Other (describe below)' },
];

export default function FlagsPage() {
  const { user, axiosAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const statusParam = searchParams.get('status');
  const [flags, setFlags] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [expandedFlag, setExpandedFlag] = useState(null);
  const [form, setForm] = useState({ client_id: '', reason: '', severity: 'medium', reason_category: '', notes: '' });
  const api = axiosAuth();
  const canCreate = ['peer_specialist', 'coordinator', 'admin'].includes(user.role);
  const canResolve = ['coordinator', 'admin'].includes(user.role);

  const load = () => {
    api.get('/flags').then(r => setFlags(r.data)).catch(() => {});
    if (canCreate) api.get('/users?role=client').then(r => setClients(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.client_id) return toast.error('Client is required');
    if (!form.reason_category && !form.reason) return toast.error('Please select a reason or describe one');
    const reasonText = form.reason_category === 'other' || !form.reason_category
      ? form.reason
      : `${COMMON_FLAG_REASONS.find(r => r.value === form.reason_category)?.label || form.reason_category}${form.reason ? ': ' + form.reason : ''}`;
    try {
      await api.post('/flags', { client_id: form.client_id, reason: reasonText, severity: form.severity, notes: form.notes });
      toast.success('Flag created!');
      setOpen(false);
      setForm({ client_id: '', reason: '', severity: 'medium', reason_category: '', notes: '' });
      load();
    } catch { toast.error('Failed to create flag'); }
  };

  const handleResolve = async (id) => {
    try {
      await api.put(`/flags/${id}/resolve`);
      toast.success('Flag resolved!');
      load();
    } catch { toast.error('Failed to resolve'); }
  };

  const sevColors = { high: 'bg-red-50 text-red-700', medium: 'bg-amber-50 text-amber-700', low: 'bg-sky-50 text-sky-700' };
  const filteredFlags = statusParam === 'resolved'
    ? flags.filter(f => f.resolved)
    : statusParam === 'active'
      ? flags.filter(f => !f.resolved)
      : flags;

  const activeFlags = flags.filter(f => !f.resolved);
  const criticalFlags = activeFlags.filter(f => f.severity === 'high');
  const clientMap = {};
  clients.forEach(c => { clientMap[c.id] = c.full_name; });

  return (
    <div className="space-y-6" data-testid="flags-page">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Risk Flags</h1><p className="text-muted-foreground text-sm mt-1">High-risk client monitoring and intervention tracking</p></div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-red-500 hover:bg-red-600 text-white rounded-full gap-2" data-testid="new-flag-button"><Plus size={16} /> Flag Client</Button></DialogTrigger>
            <DialogContent className="rounded-2xl max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Flag Participant</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="space-y-1.5"><Label>Client</Label>
                  <Select value={form.client_id} onValueChange={v => setForm({...form, client_id: v})}>
                    <SelectTrigger data-testid="flag-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                    <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Common Risk Factor</Label>
                  <Select value={form.reason_category} onValueChange={v => setForm({...form, reason_category: v})}>
                    <SelectTrigger data-testid="flag-reason-category"><SelectValue placeholder="Select a common reason..." /></SelectTrigger>
                    <SelectContent>
                      {COMMON_FLAG_REASONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Additional Details / Custom Reason</Label>
                  <DictationInput value={form.reason} onChange={(val) => setForm({...form, reason: val})} rows={3} placeholder="Describe the concern... (speak or type)" data-testid="flag-reason-dictation" showCleanup={false} />
                </div>
                <div className="space-y-1.5"><Label>Notes</Label>
                  <DictationInput value={form.notes} onChange={(val) => setForm({...form, notes: val})} rows={2} placeholder="Internal notes about this flag..." data-testid="flag-notes-dictation" showCleanup={false} />
                </div>
                <div className="space-y-1.5"><Label>Severity</Label>
                  <Select value={form.severity} onValueChange={v => setForm({...form, severity: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                  </Select>
                </div>
                <Button onClick={handleCreate} className="w-full bg-red-500 hover:bg-red-600 text-white rounded-full" data-testid="create-flag-button">Create Flag</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stat cards - clickable to filter */}
      <div className="grid grid-cols-3 gap-3">
        <Card className={`border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md transition-shadow ${!statusParam ? 'ring-2 ring-teal-300' : ''}`} onClick={() => window.history.pushState({}, '', '/flags')} data-testid="stat-total-flags">
          <CardContent className="p-4 text-center">
            <Users size={20} className="mx-auto mb-1 text-slate-500" />
            <p className="text-2xl font-bold">{flags.length}</p>
            <p className="text-xs text-muted-foreground">Total Flags</p>
          </CardContent>
        </Card>
        <Card className={`border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md transition-shadow ${statusParam === 'active' ? 'ring-2 ring-red-300' : ''}`} onClick={() => window.history.pushState({}, '', '/flags?status=active')} data-testid="stat-active-flags">
          <CardContent className="p-4 text-center">
            <AlertTriangle size={20} className="mx-auto mb-1 text-red-500" />
            <p className="text-2xl font-bold text-red-600">{activeFlags.length}</p>
            <p className="text-xs text-muted-foreground">Active</p>
          </CardContent>
        </Card>
        <Card className={`border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md transition-shadow ${statusParam === 'resolved' ? 'ring-2 ring-emerald-300' : ''}`} onClick={() => window.history.pushState({}, '', '/flags?status=resolved')} data-testid="stat-resolved-flags">
          <CardContent className="p-4 text-center">
            <CheckCircle size={20} className="mx-auto mb-1 text-emerald-500" />
            <p className="text-2xl font-bold text-emerald-600">{flags.filter(f => f.resolved).length}</p>
            <p className="text-xs text-muted-foreground">Resolved</p>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-3">
        {filteredFlags.length > 0 ? filteredFlags.map(f => (
          <Card key={f.id} className={`border-0 shadow-sm rounded-2xl ${!f.resolved ? '' : 'opacity-60'}`} data-testid={`flag-card-${f.id}`}>
            <CardContent className="p-0">
              <div className="p-4 flex items-start gap-4 cursor-pointer hover:bg-muted/20 transition-colors" onClick={() => setExpandedFlag(expandedFlag === f.id ? null : f.id)}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${f.resolved ? 'bg-emerald-50' : 'bg-red-50'}`}>
                  {f.resolved ? <CheckCircle className="w-5 h-5 text-emerald-600" /> : <Flag className="w-5 h-5 text-red-500" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{clientMap[f.client_id] || 'Client'}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{f.reason}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Flagged by {f.flagged_by_name} &middot; {formatDate(f.created_at)}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge className={sevColors[f.severity]} variant="secondary">{f.severity}</Badge>
                  {!f.resolved && canResolve && (
                    <Button size="sm" variant="outline" className="rounded-full text-xs text-emerald-600" onClick={(e) => { e.stopPropagation(); handleResolve(f.id); }} data-testid={`resolve-flag-${f.id}`}>Resolve</Button>
                  )}
                  {f.resolved && <Badge className="bg-emerald-50 text-emerald-700" variant="secondary">Resolved</Badge>}
                </div>
              </div>
              {expandedFlag === f.id && (
                <div className="px-4 pb-4 border-t space-y-2 animate-in fade-in-0 duration-200">
                  <div className="grid grid-cols-2 gap-2 text-xs pt-3">
                    <div className="bg-muted/30 rounded-lg p-2"><span className="text-muted-foreground block">Client</span><span className="font-medium">{clientMap[f.client_id] || 'N/A'}</span></div>
                    <div className="bg-muted/30 rounded-lg p-2"><span className="text-muted-foreground block">Flagged By</span><span className="font-medium">{f.flagged_by_name}</span></div>
                    <div className="bg-muted/30 rounded-lg p-2"><span className="text-muted-foreground block">Severity</span><Badge className={sevColors[f.severity]} variant="secondary">{f.severity}</Badge></div>
                    <div className="bg-muted/30 rounded-lg p-2"><span className="text-muted-foreground block">Created</span><span className="font-medium">{new Date(f.created_at).toLocaleString()}</span></div>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3 border border-red-100 text-sm">
                    <p className="text-xs font-semibold text-red-600 mb-1">Full Reason</p>
                    <p>{f.reason}</p>
                  </div>
                  {f.notes && <div className="bg-muted/30 rounded-lg p-3 text-sm"><p className="text-xs font-semibold text-muted-foreground mb-1">Internal Notes</p><p>{f.notes}</p></div>}
                  {f.resolved && f.resolved_at && (
                    <div className="bg-emerald-50 rounded-lg p-3 text-sm border border-emerald-100">
                      <p className="text-xs font-semibold text-emerald-600 mb-1">Resolution</p>
                      <p>Resolved on {new Date(f.resolved_at).toLocaleString()} {f.resolved_by_name ? `by ${f.resolved_by_name}` : ''}</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )) : (
          <Card className="border-0 shadow-sm rounded-2xl"><CardContent className="p-8 text-center"><Flag className="w-10 h-10 text-muted-foreground mx-auto mb-3" /><p className="text-muted-foreground">No flags found.</p></CardContent></Card>
        )}
      </div>
    </div>
  );
}
